import { useEffect, useState } from "react";
import useAnalyticsService from "../../../services/AnalyticsService";
import { DailyUserSignupsResult } from "../../../types/Analytics";
import {
  LineChart,
  Line,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  ResponsiveContainer,
} from "recharts";
import Ghost from "../../../components/Ghost";
import { ymd } from "../../../utilities/DateUtilities";
import { useUrlDateRange } from "../../../hooks/useUrlDateRange";
import DateRangeToolbar from "../../../components/DateRangeToolbar";
import { friendlyFormatNumber } from "../../../utilities/NumberUtilities";

export default function AdminAnalyticsUsersSummaryPage() {
  // See if start and end dates were passed in the parameters.
  const {
    getDailyUserSignups
  } = useAnalyticsService();
  const [isLoading, setIsLoading] = useState(false);
  
  const { startDate, endDate, setRange } = useUrlDateRange(7);

  const [dailyUserSignupsOverPeriod, setDailyUserSignupsOverPeriod] = useState<any[] | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) return;

    // Get the data.
    getData();
  }, [startDate, endDate]);

  const getData = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    await Promise.all([getSummaryData()]);

    setIsLoading(false);
  };

  const getSummaryData = async () => {
    const [dailySignups] = await Promise.all([
      getDailyUserSignups(startDate!, endDate!)
    ]);

    processStreamData(dailySignups);
  };

  const processStreamData = (dailySignups: DailyUserSignupsResult) => {
    // Build a list of all the dates between the start and end dates.
    var dates = getDateList();

    // Create a new list of items of {date, totalStreams}.
    var items: {
      date: Date;
      dateFriendlyName: string;
      totalSignups: number
    }[] = [];

    // Add all the dates.
    for (var date of dates) {
      items.push({
        date,
        dateFriendlyName: ymd(date),
        totalSignups: 0
      });
    }

    // Now, for each of the returned results, find the matching date and update the totals.
    for (var itemDate of dailySignups.items) {
      var streamDate = new Date(itemDate.date);
      var item = items.find(
        (item) => item.date.getUTCDate() === streamDate.getUTCDate()
      );
      if (item) {
        item.totalSignups += itemDate.count;
      }
    }

    setDailyUserSignupsOverPeriod(items);
  };

  const getDateList = (): Date[] => {
    const dateList: Date[] = [];
    const currentDate = new Date(startDate!);
    while (currentDate <= endDate!) {
      dateList.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateList;
  };

  const getSumForPeriod = () => {
    if (!dailyUserSignupsOverPeriod) return 0;
    return dailyUserSignupsOverPeriod.reduce((sum, item) => sum + item.totalSignups, 0);
  }

  return (
    <div className="grid grid-cols gap-4">
        <h2 className="text-2xl">Users</h2>
      <section className="mt-4">
        {startDate && endDate && (
          <DateRangeToolbar
            startDate={startDate}
            endDate={endDate}
            onChange={setRange}
          />
        )}
      </section>
      {!isLoading && dailyUserSignupsOverPeriod && (
        <section className="grid grid-cols-1">
          <div className="card p-4">
                      <h3 className="text-lg">Total signup (in period)</h3>
                      <p className="text-3xl font-bold">{friendlyFormatNumber(getSumForPeriod())}</p>
                    </div>
        </section>
        )}
      <section>
        <div className="grid grid-cols-1">
          <div className="grid grid-cols-1">
            <div className="w-full">
              <h3 className="text-lg">Total Signups</h3>
              <div className="w-full h-64 md:h-80 min-w-0">
                {isLoading && <Ghost additionalClasses="w-full h-full" />}
                {!isLoading && dailyUserSignupsOverPeriod && (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyUserSignupsOverPeriod}>
                      <Bar
                        name="Total signups"
                        dataKey="totalSignups"
                        fill="#60a5fa"
                      />
                      <Legend />
                      <XAxis
                        dataKey="dateFriendlyName"
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <YAxis
                        tick={{ fill: "#fff" }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <Tooltip
                        labelStyle={{ color: "#000" }} // date/header
                        itemStyle={{ color: "#000" }} // series/value rows
                        contentStyle={{
                          backgroundColor: "#fff",
                          borderColor: "#e5e7eb",
                        }} // optional
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
              
            </div>
          </div>
          <div className="p-4">
            {dailyUserSignupsOverPeriod && (
              <table className="table-auto w-full card border-spacing-2">
                <thead>
                  <tr>
                    <th className="font-normal text-left p-4">Date</th>
                    <th className="font-normal text-left p-4">Total Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyUserSignupsOverPeriod.map((item) => (
                    <tr
                      key={item.date.toString()}
                      className="border-t border-cyan-900 hover:bg-cyan-300/25"
                    >
                      <td className="p-4">
                        {item.dateFriendlyName}
                      </td>
                      <td className="p-4">
                        {item.totalSignups}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
      </section>
    </div>
  );
}
